import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupportContext } from '../../context/SupportContext';
import { useAuth } from '../../context/AuthContext';
import { getEnterAgencyPath, getEnterAgencyPathFromSupportContext } from '../../utils/agencyType';
import axios from 'axios';
import {
  Search,
  Building2,
  User,
  Eye,
  LogIn,
  ArrowLeft,
  Clock,
  Star,
  Command,
  CornerDownLeft,
  Loader2,
  X,
  Briefcase,
  Shield,
} from 'lucide-react';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Command Palette / Quick Search - Phase 9
 * 
 * Global command palette triggered by Cmd+K / Ctrl+K
 * Supports searching agencies, users, and quick context switching.
 */
const CommandPalette = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { enterAgencyContext, enterClientSupport, exitSupportMode, isInSupportMode, supportContext } = useSupportContext();
  
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ agencies: [], users: [], actions: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentContexts, setRecentContexts] = useState([]);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Load recent contexts from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentContexts');
    if (stored) {
      try {
        setRecentContexts(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse recent contexts');
      }
    }
  }, []);

  // Save recent context
  const saveRecentContext = useCallback((context) => {
    setRecentContexts(prev => {
      const filtered = prev.filter(c => c.id !== context.id);
      const updated = [context, ...filtered].slice(0, 5);
      localStorage.setItem('recentContexts', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Search API
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults({ agencies: [], users: [], actions: [] });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/owner-console/search`, {
        params: { q: searchQuery, limit: 8 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults(response.data);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Search failed:', error);
      setResults({ agencies: [], users: [], actions: [] });
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 200);
    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Build flat list of all selectable items
  const getAllItems = useCallback(() => {
    const items = [];
    
    // Default actions when no query
    if (!query.trim()) {
      if (isInSupportMode) {
        items.push({
          type: 'action',
          id: 'return-platform',
          label: 'Return to Platform Owner',
          sublabel: 'Exit current support context',
          icon: ArrowLeft,
          action: async () => {
            await exitSupportMode();
            navigate('/platform/dashboard');
            onClose();
          },
        });
      }
      
      // Show recent contexts
      recentContexts.forEach(ctx => {
        items.push({
          type: 'recent',
          id: ctx.id,
          label: ctx.name,
          sublabel: ctx.type === 'agency' ? 'Recent Agency' : `Recent Client (${ctx.role})`,
          icon: ctx.type === 'agency' ? Building2 : User,
          action: async () => {
            if (ctx.type === 'agency') {
              const result = await enterAgencyContext(ctx.id);
              navigate(
                getEnterAgencyPathFromSupportContext(result, {
                  agency_id: ctx.id,
                  agency_type: ctx.agency_type,
                })
                || getEnterAgencyPath({
                  agency_id: ctx.id,
                  agency_type: ctx.agency_type,
                })
              );
            } else {
              const result = await enterClientSupport(ctx.id);
              if (result.effective_portal_type === 'managed_client_portal') {
                navigate('/client/dashboard');
              } else {
                navigate('/dashboard');
              }
            }
            saveRecentContext(ctx);
            onClose();
          },
        });
      });
      
      return items;
    }

    // Agencies
    results.agencies.forEach(agency => {
      items.push({
        type: 'agency',
        id: agency.agency_id,
        label: agency.name,
        sublabel: `${agency.user_count || 0} users • ${agency.status || 'active'}`,
        icon: Building2,
        action: () => {
          navigate(`/platform/agencies/${agency.agency_id}`);
          onClose();
        },
        secondaryAction: async () => {
          const result = await enterAgencyContext(agency.agency_id);
          saveRecentContext({
            id: agency.agency_id,
            name: agency.name,
            type: 'agency',
            agency_type: agency.agency_type || agency.type || agency.platform_type,
          });
          navigate(
            getEnterAgencyPathFromSupportContext(result, agency)
            || getEnterAgencyPath(agency)
          );
          onClose();
        },
      });
    });

    // Users
    results.users.forEach(user => {
      const isClient = ['self_repair_customer', 'managed_client'].includes(user.role);
      items.push({
        type: 'user',
        id: user.user_id,
        label: user.name || user.email,
        sublabel: `${user.email} • ${user.role?.replace('_', ' ')}`,
        icon: isClient ? User : Shield,
        isClient,
        action: isClient ? async () => {
          const result = await enterClientSupport(user.user_id);
          saveRecentContext({ 
            id: user.user_id, 
            name: user.name || user.email, 
            type: 'client',
            role: user.role,
          });
          if (result.effective_portal_type === 'managed_client_portal') {
            navigate('/client/dashboard');
          } else {
            navigate('/dashboard');
          }
          onClose();
        } : () => {
          navigate(`/platform/agencies/${user.agency_id}`);
          onClose();
        },
      });
    });

    return items;
  }, [query, results, recentContexts, isInSupportMode, navigate, onClose, enterAgencyContext, enterClientSupport, exitSupportMode, saveRecentContext]);

  const items = getAllItems();

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = items[selectedIndex];
        if (item?.action) {
          item.action();
        }
      } else if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        const item = items[selectedIndex];
        if (item?.secondaryAction) {
          item.secondaryAction();
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, items, selectedIndex, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selected = resultsRef.current.querySelector('[data-selected="true"]');
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" data-testid="command-palette">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Command Palette Modal - Glassmorphism */}
      <div 
        className="relative w-full max-w-2xl mx-4 rounded-2xl overflow-hidden shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(148, 163, 184, 0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)',
        }}
        data-testid="command-palette-modal"
      >
        {/* Search Input */}
        <div className="flex items-center px-5 py-4 border-b border-white/5">
          <Search className="text-slate-400 mr-3" size={20} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search agencies, clients, or actions..."
            className="flex-1 bg-transparent text-white text-lg placeholder:text-slate-500 outline-none"
            data-testid="command-palette-input"
          />
          {isLoading && <Loader2 className="text-emerald-400 animate-spin" size={20} />}
          <button 
            onClick={onClose}
            className="ml-3 p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Results */}
        <div ref={resultsRef} className="max-h-[400px] overflow-y-auto overscroll-contain py-2">
          {items.length === 0 && query.length >= 2 && !isLoading && (
            <div className="px-5 py-8 text-center text-slate-400">
              No results found for "{query}"
            </div>
          )}

          {items.length === 0 && query.length < 2 && recentContexts.length === 0 && (
            <div className="px-5 py-8 text-center text-slate-400">
              <p className="mb-2">Type to search agencies and clients</p>
              <p className="text-sm text-slate-500">Or use keyboard shortcuts for quick actions</p>
            </div>
          )}

          {/* Recent Contexts Section */}
          {!query.trim() && recentContexts.length > 0 && (
            <div className="px-3 pb-2">
              <div className="px-2 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <Clock size={12} />
                Recent
              </div>
            </div>
          )}

          {/* Return to Platform Action */}
          {!query.trim() && isInSupportMode && (
            <div className="px-3 pb-2">
              <div className="px-2 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <Command size={12} />
                Quick Actions
              </div>
            </div>
          )}

          {items.map((item, index) => {
            const Icon = item.icon;
            const isSelected = index === selectedIndex;
            
            return (
              <div
                key={`${item.type}-${item.id}`}
                data-selected={isSelected}
                onClick={() => item.action?.()}
                className={`mx-3 px-3 py-3 rounded-xl cursor-pointer transition-all flex items-center gap-4 group ${
                  isSelected 
                    ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30' 
                    : 'hover:bg-white/5 border border-transparent'
                }`}
              >
                {/* Icon */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                  isSelected 
                    ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/25' 
                    : 'bg-slate-700/50 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-300'
                }`}>
                  <Icon size={20} />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className={`font-medium truncate ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                    {item.label}
                  </div>
                  <div className="text-sm text-slate-400 truncate">
                    {item.sublabel}
                  </div>
                </div>

                {/* Actions hints */}
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {item.type === 'agency' && (
                    <>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <CornerDownLeft size={12} />
                        View
                      </span>
                      {item.secondaryAction && (
                        <span className="text-xs text-slate-500 flex items-center gap-1 border-l border-slate-600 pl-2">
                          Tab → Enter
                        </span>
                      )}
                    </>
                  )}
                  {item.type === 'user' && item.isClient && (
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Eye size={12} />
                      Support View
                    </span>
                  )}
                  {item.type === 'action' && (
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <CornerDownLeft size={12} />
                      Execute
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Search Results Sections */}
          {query.trim() && results.agencies.length > 0 && (
            <div className="px-5 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider">
              Agencies
            </div>
          )}
          
          {query.trim() && results.users.length > 0 && results.agencies.length > 0 && (
            <div className="px-5 py-2 mt-2 text-xs font-medium text-slate-500 uppercase tracking-wider border-t border-white/5 pt-4">
              Users & Clients
            </div>
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">↵</kbd>
              Select
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">esc</kbd>
              Close
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-emerald-400">
            <Command size={12} />
            <span>K to open</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
